/**
 * One breaker: what it is, how far along it is, its photos and its checks.
 *
 * Readings and photos are on the same page on purpose. Splitting them across two
 * screens is what the paper process already does badly — the engineer takes the
 * reading, walks away, and the photo is remembered or it is not.
 */

import { memo } from 'react';
import type { Asset, CapturedShot, ShotProfile, TemplateItem } from '../domain/types.js';
import type { ResultInput } from '../domain/verdict.js';
import type { ScopeProgress } from '../domain/capture.js';
import { CheckRow } from './CheckRow.js';
import { PhotoGrid } from './PhotoGrid.js';

export interface BreakerPageProps {
  breaker: Asset;
  board: Asset;
  view: ScopeProgress;
  profiles: readonly ShotProfile[];
  captured: Readonly<Record<string, CapturedShot>>;
  onAnswer: (item: TemplateItem, input: ResultInput) => void;
  onCapture: (profile: ShotProfile) => void;
  /** Item ids whose acceptance limit was moved from the project default. */
  movedLimits?: ReadonlySet<number>;
  disabled?: boolean;
}

export const BreakerPage = memo(function BreakerPage({
  breaker, board, view, profiles, captured, onAnswer, onCapture, movedLimits, disabled,
}: BreakerPageProps) {
  const ref = breaker.tag.split('/').pop() ?? breaker.tag;
  const unknownType = breaker.breakerType === 'N/A';

  return (
    <article className="breaker">
      <header>
        <h2>{ref}</h2>
        <p className="tag">{breaker.tag}</p>
        <div className="facts">
          {unknownType ? (
            // On the one-line diagram, absent from the settings table. It exists
            // and must be testable; what it needs photographed cannot be known
            // until someone reads the board.
            <span className="chip pend">Type unknown — confirm on the board</span>
          ) : (
            <span className="chip na">{breaker.breakerType}</span>
          )}
          {breaker.model ? <span className="chip na">{breaker.model}</span> : null}
          {breaker.serialNumber ? (
            <span className="chip na">S/N {breaker.serialNumber}</span>
          ) : (
            <span className="chip pend">Serial not recorded</span>
          )}
        </div>
        <p className="parent">on {board.tag}</p>
      </header>

      {/* A flex row, not raw spans: unstyled they render as
          "…checks answered0 of 52 photos" with no separator. */}
      <div className="progress" data-testid="progress">
        <span data-testid="checks-progress">
          {view.overall.total - view.overall.pending} of {view.overall.total} checks answered
        </span>
        {view.shots ? (
          <span data-testid="photos-progress">
            {view.shots.captured} of {view.shots.required} photos
          </span>
        ) : null}
        <span className={`chip ${view.overall.overall.toLowerCase().replace('/', '')}`} data-testid="overall">
          {view.overall.overall}
        </span>
      </div>

      {view.shots ? (
        <PhotoGrid
          profiles={profiles}
          captured={captured}
          progress={view.shots}
          onCapture={onCapture}
          {...(disabled !== undefined ? { disabled } : {})}
        />
      ) : null}

      {view.sections.map((section) => (
        <section className="script-section" key={section.section.id} aria-label={section.section.name}>
          <h3>
            {section.section.name}
            <span className={`chip ${section.rollup.overall.toLowerCase().replace('/', '')}`}>
              {section.rollup.overall}
            </span>
          </h3>
          {section.checks.map((check) => (
            <CheckRow
              key={check.item.id}
              check={check}
              onChange={(input) => onAnswer(check.item, input)}
              {...(movedLimits?.has(check.item.id) ? { limitMoved: true } : {})}
              {...(disabled !== undefined ? { disabled } : {})}
            />
          ))}
        </section>
      ))}

      {view.sections.length === 0 ? (
        <p className="empty">No breaker-level checks in this script.</p>
      ) : null}
    </article>
  );
});
